import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface AIChatContextValue {
  isOpen: boolean;
  activeConversationId: string | null;
  isDraft: boolean;
  openChat: (conversationId?: string) => void;
  closeChat: () => void;
  startNewConversation: () => void;
  activateConversation: (id: string) => void;
}

const AIChatContext = createContext<AIChatContextValue | null>(null);

export function AIChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState(false);

  const openChat = useCallback((conversationId?: string) => {
    setActiveConversationId(conversationId ?? null);
    setIsDraft(false);
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setIsDraft(true);
    setIsOpen(true);
  }, []);

  const activateConversation = useCallback((id: string) => {
    setActiveConversationId(id);
    setIsDraft(false);
  }, []);

  const value = useMemo(
    () => ({ isOpen, activeConversationId, isDraft, openChat, closeChat, startNewConversation, activateConversation }),
    [isOpen, activeConversationId, isDraft, openChat, closeChat, startNewConversation, activateConversation],
  );

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
}

export function useAIChat(): AIChatContextValue {
  const ctx = useContext(AIChatContext);
  if (!ctx) throw new Error('useAIChat must be used within AIChatProvider');
  return ctx;
}
